import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { logException } from "@wss/error-tracking";
import { Throwable } from "@wss/error-tracking/throwable";
import { env } from "~/common/Environment";

type Recaptcha = {
  execute: (key: string, opts?: { action?: string }) => Promise<string>;
  ready: (cb: () => void) => void;
};

const recaptchaSiteKeys = {
  local: "6Lf0ILAUAAAAAAG_5Z5G2ihgXtxsjvw6CKYt3OHs",
  development: "6Lf0ILAUAAAAAAG_5Z5G2ihgXtxsjvw6CKYt3OHs",
  test: "6Lf0ILAUAAAAAAG_5Z5G2ihgXtxsjvw6CKYt3OHs",
  production: "6LcMB7EUAAAAAIBgYhbNBrCNyKHlQtMgVYTjpT8V",
};

/** Preflight uses the dev key */
const recaptchaSiteKey =
  recaptchaSiteKeys[env.isPreflight ? "development" : env.current];
const scriptId = "google-recaptcha-v3";

export type RecaptchaContextType = {
  /** See {@link useReCaptcha } for more information on how to use ReCaptcha. */
  execute: (action?: string) => Promise<Throwable<string, unknown>>;
  /** Allows a consumer to wait for the re-captcha script to be loaded */
  onLoad: (cb: () => void) => void;
};

export const RecaptchaContext = createContext<RecaptchaContextType | null>(
  null,
);

type ReCaptchaProviderProps = {
  action?: string;
  language?: string;
  useEnterprise?: boolean;
  children?: ReactNode;
};

/**
 * Provides all pages in SF with the ability to use ReCaptcha.
 *
 * See {@link useReCaptcha } for more information on how to use ReCaptcha.
 *
 * {@link https://developers.google.com/recaptcha/docs/v3 ReCaptcha Documentation}
 *
 * {@link https://github.com/t49tran/react-google-recaptcha-v3 Github Reference}
 */
export function ReCaptchaProvider({
  action: defaultAction,
  useEnterprise = true,
  language,
  children,
}: ReCaptchaProviderProps): JSX.Element {
  const instanceRef = useRef<Recaptcha | null>(null);
  const subs = useRef<Set<() => void>>(new Set());

  const onLoad: RecaptchaContextType["onLoad"] = useCallback(
    function handleOnLoad(cb: () => void): void {
      if (!instanceRef.current) {
        subs.current.add(cb);
        return;
      }
      cb();
    },
    [],
  );

  const execute: RecaptchaContextType["execute"] = useCallback(
    async function onExecute(
      action?: string,
    ): ReturnType<RecaptchaContextType["execute"]> {
      if (!instanceRef.current) {
        const error = new Error("Google Recaptcha has not been loaded");
        await logException(error);
        return { isError: true, error };
      }

      try {
        const result = await instanceRef.current.execute(recaptchaSiteKey, {
          action: action ?? defaultAction,
        });
        return { isError: false, value: result };
      } catch (error) {
        await logException(error);
        return { isError: true, error };
      }
    },
    [defaultAction],
  );

  const handleLoad = useCallback(
    async function onLoad(): Promise<void> {
      if (!window.grecaptcha) {
        await logException(new Error("Recaptcha was no able to load"));
        return;
      }

      const ref: Recaptcha = useEnterprise
        ? window.grecaptcha.enterprise
        : window.grecaptcha;

      ref.ready(function handleRecaptchaReady() {
        instanceRef.current = ref;
        subs.current.forEach((sub) => sub());
        subs.current.clear();
      });
    },
    [useEnterprise],
  );

  const handleError = useCallback(async function onError() {
    // We ignore the error because the execute function will return a Throwable error allowing the consumer to determine how to notify the user that re-captcha has failed to load.
  }, []);

  const handleUnload = useCallback(
    function onUnload() {
      const nodeBadge = document.querySelector(".grecaptcha-badge");
      if (nodeBadge && nodeBadge.parentNode) {
        document.body.removeChild(nodeBadge.parentNode);
      }

      instanceRef.current = null;

      const js = document.querySelector<HTMLScriptElement>(`#${scriptId}`);
      js?.removeEventListener("load", handleLoad);
      js?.removeEventListener("error", handleError);
    },
    [handleError, handleLoad],
  );

  useEffect(
    function initRecaptchaScript() {
      const script = document.querySelector(`#${scriptId}`);
      if (script) {
        if (window.grecaptcha) {
          // The ReCaptcha script has already loaded and executed
          handleLoad();
        } else {
          // Wait until the script finishes loading
          script.addEventListener("load", handleLoad);
        }
        return handleUnload;
      }

      const scriptName = useEnterprise ? "enterprise.js" : "api.js";
      const url = new URL(`https://www.google.com/recaptcha/${scriptName}`);
      url.searchParams.append("render", recaptchaSiteKey);
      if (language) {
        url.searchParams.append("hl", language);
      }
      const js = document.createElement("script");
      js.id = scriptId;
      js.src = url.href;
      js.addEventListener("load", handleLoad);
      js.addEventListener("error", handleError);

      document.head.appendChild(js);

      return handleUnload;
    },
    [handleError, handleLoad, handleUnload, language, useEnterprise],
  );

  const value = useMemo(() => ({ execute, onLoad }), [execute, onLoad]);

  return (
    <RecaptchaContext.Provider value={value}>
      {children}
    </RecaptchaContext.Provider>
  );
}
